import React, { useEffect } from "react";
import { InputOTP, InputOTPGroup, InputOTPSeparator, InputOTPSlot } from "ui/InputOTP";
import { Loader2, Sparkles, Ticket } from "lucide-react";
import type { QuizUiStrings } from "i18n/quizUi";
import { getQuizButtonClassName } from "./quizUtils";

const QUIZ_CODE_PATTERN = "^[0-9a-zA-Z]+$";

function normalizeQuizCodeInput(value: string): string {
  return value.replace(/[^0-9a-z]/gi, "").slice(0, 8).toUpperCase();
}

interface QuizLandingProps {
  ui: QuizUiStrings;
  codeValue: string;
  onCodeChange: (value: string) => void;
  onStart: () => void;
  onJoin: () => void;
  isCreating: boolean;
  isJoining: boolean;
}

const cardClassName =
  "rounded-2xl border border-eoe-border-main bg-eoe-bg-modal p-5 shadow-sm dark:border-eoe-dark-border-main dark:bg-eoe-dark-bg-modal";

/**
 * Start-or-join landing surface for `/quiz`.
 */
const QuizLanding: React.FC<QuizLandingProps> = ({
  ui,
  codeValue,
  onCodeChange,
  onStart,
  onJoin,
  isCreating,
  isJoining,
}) => {
  useEffect(() => {
    const normalizedCodeValue = normalizeQuizCodeInput(codeValue);
    if (normalizedCodeValue.length < 6 || isJoining) {
      return;
    }

    const timeout = window.setTimeout(() => {
      onJoin();
    }, 500);

    return () => {
      window.clearTimeout(timeout);
    };
  }, [codeValue, isJoining, onJoin]);

  return (
    <div className="mx-auto flex w-full max-w-5xl flex-col gap-6 px-4 py-8">
      <div className="space-y-3 text-center">
        <h1 className="text-3xl font-semibold text-eoe-text-heading dark:text-eoe-dark-text-heading">
          {ui.landingTitle}
        </h1>
        <p className="mx-auto max-w-3xl text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
          {ui.landingDescription}
        </p>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <section className={cardClassName}>
          <div className="mb-4 flex items-start gap-3">
            <div className="rounded-full bg-eoe-blue/10 p-3 text-eoe-blue dark:bg-eoe-blue/20">
              <Sparkles className="size-5" aria-hidden />
            </div>
            <div className="space-y-1">
              <h2 className="text-lg font-semibold text-eoe-text dark:text-eoe-dark-text">{ui.startNewQuiz}</h2>
              <p className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">{ui.startNewQuizBody}</p>
            </div>
          </div>

          <button
            type="button"
            onClick={onStart}
            disabled={isCreating}
            className={getQuizButtonClassName({
              disabled: isCreating,
              fullWidth: true,
            })}
          >
            {isCreating ? <Loader2 className="size-4 animate-spin" aria-hidden /> : null}
            {isCreating ? ui.loadingQuiz : ui.createQuizAction}
          </button>
        </section>

        <section className={cardClassName}>
          <div className="mb-4 flex items-start gap-3">
            <div className="rounded-full bg-eoe-blue/10 p-3 text-eoe-blue dark:bg-eoe-blue/20">
              <Ticket className="size-5" aria-hidden />
            </div>
            <div className="space-y-1">
              <h2 className="text-lg font-semibold text-eoe-text dark:text-eoe-dark-text">{ui.enterCodeTitle}</h2>
              <p className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">{ui.enterCodeBody}</p>
            </div>
          </div>

          <label className="mb-2 block text-sm font-semibold text-eoe-text dark:text-eoe-dark-text">
            {ui.enterCodeLabel}
          </label>
          <div className="flex flex-col gap-3">
            <div className="flex justify-center sm:justify-start">
              <InputOTP
                maxLength={8}
                pattern={QUIZ_CODE_PATTERN}
                inputMode="text"
                value={normalizeQuizCodeInput(codeValue)}
                aria-label={ui.enterCodeLabel}
                autoFocus={false}
                onChange={(value) => {
                  onCodeChange(normalizeQuizCodeInput(value));
                }}
                containerClassName="group flex items-center"
                disabled={isJoining}
              >
                <InputOTPGroup>
                  <InputOTPSlot index={0} />
                  <InputOTPSlot index={1} />
                  <InputOTPSlot index={2} />
                  <InputOTPSlot index={3} />
                </InputOTPGroup>
                <InputOTPSeparator />
                <InputOTPGroup>
                  <InputOTPSlot index={4} />
                  <InputOTPSlot index={5} />
                  <InputOTPSlot index={6} />
                  <InputOTPSlot index={7} />
                </InputOTPGroup>
              </InputOTP>
            </div>
            <button
              type="button"
              onClick={onJoin}
              disabled={isJoining}
              className={getQuizButtonClassName({
                disabled: isJoining,
              })}
            >
              {isJoining ? <Loader2 className="size-4 animate-spin" aria-hidden /> : null}
              {ui.joinQuizAction}
            </button>
          </div>
        </section>
      </div>
    </div>
  );
};

export default QuizLanding;
